import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import orderService from '@services/order';

/**
 * Transform store data from API response to table structure
 */
const transformStoreData = (data, periodType) => {
  if (!data || !data.data) return [];

  const flattenData = {};

  data.data.forEach((store) => {
    flattenData[store.store.id] = {
      id: store.store.id,
      storeName: store.store.name,
      tickets: {
        [periodType]: store.transactionCount,
      },
      turnoverExcludingTaxes: {
        [periodType]: store.turnover,
      },
      realFoodConsumption: {
        [periodType]: store.foodCost?.realConsumption,
      },
      realFoodConsumptionRatio: {
        [periodType]:
          store.foodCost?.realConsumption && store.turnover
            ? Math.round((store.foodCost.realConsumption / store.turnover) * 100 * 100) / 100
            : 0,
      },
    };
  });

  return flattenData;
};

/**
 * Fetch global analytics data for stores
 */
export const fetchGlobalData = async (selectedStores, currentPeriod, previousPeriod) => {
  const storeIds = selectedStores.map((store) => store.id);

  // Fetch data for current period
  const [currentPeriodData, previousPeriodData] = await Promise.all([
    orderService.getOrdersAnalyticsByStore(
      storeIds,
      currentPeriod.startDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      currentPeriod.endDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
    ),
    orderService.getOrdersAnalyticsByStore(
      storeIds,
      previousPeriod.startDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      previousPeriod.endDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
    ),
  ]);

  const currentData = transformStoreData(currentPeriodData, 'current');
  const previousData = transformStoreData(previousPeriodData, 'previous');

  // Merge current and previous data
  const currentAndPreviousStoreIds = [
    ...new Set([...Object.keys(currentData), ...Object.keys(previousData)]),
  ];

  const mergedGlobalData = currentAndPreviousStoreIds.map((id) => {
    const currentStore = currentData[id];
    const previousStore = previousData[id];
    return {
      id,
      storeName: currentStore?.storeName || previousStore?.storeName,
      tickets: {
        current: currentStore?.tickets?.current,
        previous: previousStore?.tickets?.previous,
      },
      turnoverExcludingTaxes: {
        current: currentStore?.turnoverExcludingTaxes?.current,
        previous: previousStore?.turnoverExcludingTaxes?.previous,
      },
      realFoodConsumption: {
        current: currentStore?.realFoodConsumption?.current,
        previous: previousStore?.realFoodConsumption?.previous,
      },
      realFoodConsumptionRatio: {
        current: currentStore?.realFoodConsumptionRatio?.current,
        previous: previousStore?.realFoodConsumptionRatio?.previous,
      },
      averageTicket: {
        current: currentStore?.tickets?.current
          ? currentStore?.turnoverExcludingTaxes?.current / currentStore?.tickets?.current
          : null,
        previous: previousStore?.tickets?.previous
          ? previousStore?.turnoverExcludingTaxes?.previous / previousStore?.tickets?.previous
          : null,
      },
    };
  });

  // Compute global data from real table data
  const sumData = mergedGlobalData.reduce(
    (acc, row) => ({
      tickets: {
        current: acc.tickets.current + (row.tickets.current || 0),
        previous: acc.tickets.previous + (row.tickets.previous || 0),
      },
      turnoverExcludingTaxes: {
        current: acc.turnoverExcludingTaxes.current + (row.turnoverExcludingTaxes.current || 0),
        previous: acc.turnoverExcludingTaxes.previous + (row.turnoverExcludingTaxes.previous || 0),
      },
      realFoodConsumption: {
        current: acc.realFoodConsumption.current + (row.realFoodConsumption.current || 0),
        previous: acc.realFoodConsumption.previous + (row.realFoodConsumption.previous || 0),
      },
    }),
    {
      tickets: {
        current: 0,
        previous: 0,
      },
      turnoverExcludingTaxes: {
        current: 0,
        previous: 0,
      },
      realFoodConsumption: {
        current: 0,
        previous: 0,
      },
    },
  );

  const globalData = {
    ...sumData,
    realFoodConsumptionRatio: {
      current:
        sumData.turnoverExcludingTaxes.current !== 0
          ? (sumData.realFoodConsumption.current / sumData.turnoverExcludingTaxes.current) * 100
          : null,
      previous:
        sumData.turnoverExcludingTaxes.previous !== 0
          ? (sumData.realFoodConsumption.previous / sumData.turnoverExcludingTaxes.previous) * 100
          : null,
    },
    storeCount: {
      current: mergedGlobalData.filter(
        ({ turnoverExcludingTaxes }) => turnoverExcludingTaxes.current > 0,
      ).length,
      previous: mergedGlobalData.filter(
        ({ turnoverExcludingTaxes }) => turnoverExcludingTaxes.previous > 0,
      ).length,
    },
    averageTicket: {
      current: sumData.tickets.current
        ? sumData.turnoverExcludingTaxes.current / sumData.tickets.current
        : null,
      previous: sumData.tickets.previous
        ? sumData.turnoverExcludingTaxes.previous / sumData.tickets.previous
        : null,
    },
  };

  return {
    globalData,
    globalTableData: mergedGlobalData,
  };
};

/**
 * Fetch time series data for a specific store and metric
 */
export const fetchTimeSeriesData = async (storeId, currentPeriod, previousPeriod) => {
  // Prepare all API requests for parallel execution
  const apiRequests = [];

  const currentPeriodDays = currentPeriod.endDate.diff(currentPeriod.startDate, 'days');
  for (let i = 0; i <= currentPeriodDays; i++) {
    const date = currentPeriod.startDate.clone().add(i, 'days');

    apiRequests.push({
      period: 'current',
      date,
      promise: orderService.getOrdersAnalyticsByStore(
        [storeId],
        date.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
        date.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      ),
    });
  }

  const previousPeriodDays = previousPeriod.endDate.diff(previousPeriod.startDate, 'days');
  for (let i = 0; i <= previousPeriodDays; i++) {
    const date = previousPeriod.startDate.clone().add(i, 'days');

    apiRequests.push({
      period: 'previous',
      date,
      promise: orderService.getOrdersAnalyticsByStore(
        [storeId],
        date.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
        date.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      ),
    });
  }

  const results = await Promise.all(
    apiRequests.map(async (request) => {
      const response = await request.promise;
      return {
        ...request,
        data: {
          turnoverExcludingTaxes: response.totalData.turnover,
          realFoodConsumption: response.totalData.foodCost.realConsumption,
          realFoodConsumptionRatio:
            response.totalData.turnover !== 0
              ? (response.totalData.foodCost.realConsumption / response.totalData.turnover) * 100
              : 0,
          tickets: response.totalData.transactionCount,
          averageTicket:
            response.totalData.transactionCount !== 0
              ? response.totalData.turnover / response.totalData.transactionCount
              : null,
        },
      };
    }),
  );

  const previousPeriodResults = results.filter((result) => result.period === 'previous');
  const currentPeriodResults = results.filter((result) => result.period === 'current');

  let data = [];

  for (let i = 0; i <= Math.max(previousPeriodDays, currentPeriodDays); i++) {
    data.push({
      index: Math.max(previousPeriodDays, currentPeriodDays) - i,
      date: currentPeriod.endDate.clone().subtract(i, 'days'),
      current: i <= currentPeriodDays ? currentPeriodResults[currentPeriodDays - i]?.data : 0,
      previous: i <= previousPeriodDays ? previousPeriodResults[previousPeriodDays - i]?.data : 0,
      formattedDates: {
        current: currentPeriod.endDate
          .clone()
          .subtract(i, 'days')
          .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
        previous: previousPeriod.endDate
          .clone()
          .subtract(i, 'days')
          .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      },
    });
  }

  // Sort results by date to maintain chronological order
  return data.sort((a, b) => a.index - b.index);
};
